import { yupResolver } from "@hookform/resolvers/yup";
import { endOfMonth, isBefore, isSameDay } from "date-fns";
import { t } from "i18next";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import useAsyncEffect from "use-async-effect";
import * as yup from "yup";

import { getDataFromResponse } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button";
import DatePickerInput from "~/components/formElements/DatePickerInput";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import FormInputError from "~/components/formElements/FormInputError";
import Input from "~/components/formElements/Input";
import Label from "~/components/formElements/Label";
import SubmitButton from "~/components/formElements/SubmitButton";
import Textarea from "~/components/formElements/Textarea";
import Headline from "~/components/Headline";
import Hint from "~/components/Hint";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import {
	getPendingInvoiceStatusReport,
	updatePendingSelfBilledInvoiceStatusToDraft,
} from "~/modules/billing/api/pendingInvoice/pendingInvoiceApiDispatchers.ts";
import type { UnprocessedInvoiceStatusReportData } from "~/modules/billing/api/unprocessedInvoicesTypes.ts";
import InvoiceTotalsFormSection from "~/modules/billing/components/InvoiceTotalsFormSection";
import type { UnprocessedInvoicesIndexTableData } from "~/modules/billing/components/unprocessedInvoicesTableTypes.ts";
import UsersMonthlyClosingStatus from "~/modules/billing/components/UsersMonthlyClosingStatus";
import { DEFAULT_VAT_PERCENTAGE } from "~/modules/billing/constants/billingConstants.ts";
import type { User, UserWithConfidentialInformation } from "~/modules/user/api/user/userTypes.ts";
import { formatDateToYYYYMMDD, formatSmartDateRange } from "~/utils/dateAndTimeUtils.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type FormData = {
	comment: string | null;
	deliveryStartDate: Date;
	deliveryEndDate: Date;
	invoiceNumber: string;
	invoiceReceivedAt:Date;
	senderInformation: string | null;
	subtotalCents: number;
	travelCostCentsBeforeVat: number;
	vatPercentage: number;
}

type Props = {
	allUsersData: User[] | UserWithConfidentialInformation[];
	onCancel: () => void;
	onSuccess: () => void;
	pendingInvoiceData: UnprocessedInvoicesIndexTableData;
};

const endOfCurrentMonth = endOfMonth(new Date());

const ProcessSelfBilledDraftInvoiceForm = ({
	allUsersData,
	onCancel,
	onSuccess,
	pendingInvoiceData,
}: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");
	const [isPerformingPreCheck, setIsPerformingPreCheck] = useState<boolean>(false);
	const [updatePreCheckData, setUpdatePreCheckData] = useState<UnprocessedInvoiceStatusReportData | null>(null);

	const schema = useMemo(() => {
		return yup.object({
			comment: yup.string().nullable().default(""),
			deliveryStartDate: yup.date().required(),
			deliveryEndDate: yup
				.date()
				.required()
				.min(yup.ref("deliveryStartDate"), "Das Startdatum muss vor dem Enddatum liegen.")
				.default(null),
			invoiceNumber: yup.string().required(),
			invoiceReceivedAt: yup.date().required(),
			senderInformation: yup.string().nullable().default(""),
			subtotalCents: yup.number().required(),
			travelCostCentsBeforeVat: yup.number().required(),
			vatPercentage: yup.number().required(),
		});
	}, []);
	const defaultValues = useMemo(() => {
		return {
			comment: pendingInvoiceData.comment || "",
			deliveryStartDate: pendingInvoiceData.deliveryStartDate,
			deliveryEndDate: pendingInvoiceData.deliveryEndDate,
			invoiceNumber: "",
			invoiceReceivedAt: new Date(),
			senderInformation: "",
			subtotalCents: pendingInvoiceData.invoiceTotalCents,
			travelCostCentsBeforeVat: 0,
			vatPercentage: DEFAULT_VAT_PERCENTAGE,
		};
	}, [pendingInvoiceData.comment, pendingInvoiceData.deliveryEndDate, pendingInvoiceData.deliveryStartDate, pendingInvoiceData.invoiceTotalCents]);

	const {
		handleSubmit,
		control,
		formState: { isSubmitted, isValid },
		setValue,
		watch,
	} = useForm<FormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<FormData>(schema),
		mode: "onChange",
	});

	const deliveryStartDate = watch("deliveryStartDate");
	const deliveryEndDate = watch("deliveryEndDate");
	const dateChanged = !isSameDay(deliveryStartDate, pendingInvoiceData.deliveryStartDate) || !isSameDay(deliveryEndDate, pendingInvoiceData.deliveryEndDate);

	useAsyncEffect(async (isMounted) => {
		if (!dateChanged) {
			setUpdatePreCheckData(null);
			setIsPerformingPreCheck(false);
			return;
		}
		if (isBefore(deliveryEndDate, deliveryStartDate)) return;

		setIsPerformingPreCheck(true);
		const startDate = formatDateToYYYYMMDD(deliveryStartDate);
		const endDate = formatDateToYYYYMMDD(deliveryEndDate);
		const preCheckResponse = await getPendingInvoiceStatusReport(pendingInvoiceData.id, startDate, endDate);
		if (!isMounted()) return;
		const preCheckData = getDataFromResponse(preCheckResponse);
		setUpdatePreCheckData(preCheckData);
		setValue('subtotalCents', preCheckData.invoiceTotalCents);
		setIsPerformingPreCheck(false);
	}, [dateChanged, deliveryEndDate, deliveryStartDate]);
	const formIsSubmittable = !isSubmitted || (isValid && !isBusy);
	const hasConflicts = updatePreCheckData?.conflicts && updatePreCheckData.conflicts.length > 0;
	const invoiceTotalIsInvalid = updatePreCheckData?.invoiceTotalCents === 0;
	const allStaffingsClosed = updatePreCheckData ? updatePreCheckData.allStaffingsClosed : pendingInvoiceData.allStaffingsClosed;

	const onSubmit = useCallback(async (data: FormData) => {
		if(!isValid || !allStaffingsClosed  || invoiceTotalIsInvalid){
			return;
		}
		try {
			setIsBusy(true);
			const updateData = {
				...data,
				deliveryEndDate: formatDateToYYYYMMDD(data.deliveryEndDate),
				deliveryStartDate: formatDateToYYYYMMDD(data.deliveryStartDate),
				receivedAt: formatDateToYYYYMMDD(data.invoiceReceivedAt),
			};

			await updatePendingSelfBilledInvoiceStatusToDraft(pendingInvoiceData.id, updateData);
			onSuccess();
		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
			setIsBusy(false);
		}
	}, [isValid, allStaffingsClosed, invoiceTotalIsInvalid, pendingInvoiceData.id, onSuccess]);



	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="auto-grid-sidebar"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Details Kundengutschrift</SidebarHeaderHeadline>
			</SidebarHeader>
			<div className="grow gap-x-8 overflow-auto px-7 py-2">
				{(isBusy || isPerformingPreCheck) && <SidebarBusyOverlay />}
				{serverErrorMessage &&
					<SidebarErrorOverlay title="Update fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}

				<div className="grid h-auto grid-cols-2 gap-4 pt-1">
					<div className="col-span-2">
						<Headline className="mb-2"
								  type="h4">Gutschrift- / Referenznummer </Headline>
						<Input control={control}
							   name="invoiceNumber"
							   placeholder="Rechnungsnummer Kunde"
						/>
						<div className="mb-4 mt-2 text-xs text-gray-400">Hier bitte die Rechnungsnummer eintragen, die auf
							der Gutschrift des Kunden zu finden ist.
						</div>
						<div className="grid grid-cols-2 gap-x-4">
							<Input control={control}
								   name="senderInformation"
								   label="Absender"
								   placeholder="Name des Absenders, ggf. E-Mail-Adresse"
							/>
							<DatePickerInput name="invoiceReceivedAt"
											 clearable={false}
											 control={control}
											 label="Eingegangen am" />
						</div>

					</div>
					<div className="col-span-2">
						<Headline className="mb-2"
								  type="h4">Leistungszeitraum</Headline>
						<div className="grid w-full grid-cols-2 gap-x-4">
							<DatePickerInput name="deliveryStartDate"
											 clearable={false}
											 control={control}
											 label="Start" />

							<DatePickerInput name="deliveryEndDate"
											 clearable={false}
											 control={control}
											 maxDate={endOfCurrentMonth}
											 label="Ende" />
							{isValid && !isPerformingPreCheck && hasConflicts && <div className="col-span-2">
								<FormInputError>In dem ausgewählten Leistungszeitraum existieren bereits Rechnungen
									für
									diese Bestellung:</FormInputError>
								{updatePreCheckData?.conflicts.map((conflict, index) => (
									<div key={index}
										 className="my-2 flex w-full items-center justify-between rounded border border-red-500 p-2 text-sm text-red-500">
										<div>{conflict.invoiceNumber}</div>
										<div>{formatSmartDateRange(new Date(conflict.deliveryStartDate), new Date(conflict.deliveryEndDate), {
											month: "MMMM",
											year: " yyyy",
										})}</div>
									</div>
								))}
							</div>}
							{isValid && !isPerformingPreCheck && invoiceTotalIsInvalid &&
								<div className="col-span-2">
									<FormInputError>In diesem Zeitraum liegen keine abrechenbaren Erfassungen
										vor.</FormInputError>
								</div>}
						</div>
						<div className="mb-1 mt-4">
							<Label>Status Monatsabschluss im Leistungszeitraum</Label>
						</div>
						<div className="col-span-2 flex w-full flex-row flex-wrap gap-1">
							<UsersMonthlyClosingStatus allUsersData={allUsersData}
													   staffingsClosingStatuses={updatePreCheckData ? updatePreCheckData.staffingsClosingStatuses : pendingInvoiceData.staffingsClosingStatuses} />
							{isSubmitted && !allStaffingsClosed && <div className="mt-2"><Hint size="sm"
																				theme="error">Der Abrechnungsvorgang
								kann erst
								gestartet werden, wenn alle Teammitglieder ihre Monatsabschlüsse für den gesamten
								Leistungszeitraum
								getätigt haben.</Hint></div>}
						</div>
					</div>

					<InvoiceTotalsFormSection<FormData> control={control}
														watch={watch}
														title="Rechnungsbetrag" />
					<div className="col-span-2 mt-4">
						<Headline className="mb-2"
								  type="h4">Kommentar</Headline>
						<Textarea control={control}
								  name="comment"
								  placeholder="Hier können Besonderheiten zum Abrechnungsprozess vermerkt werden."
						/>
					</div>
				</div>
			</div>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && (!isValid || !allStaffingsClosed)}
								   className="mr-2" />
				<SubmitButton busy={isBusy}
							  disabled={!formIsSubmittable}>
					Rechnungsstatus in &quot;Nicht bezahlt&quot; ändern
				</SubmitButton>
				<Button theme="white"
						onClick={onCancel}>
					Abbrechen
				</Button>
			</SidebarFooter>
		</form>);
};

export default ProcessSelfBilledDraftInvoiceForm;
